import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarController,
  LineController,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import dbManagerInterface from "../../Utility/DbConnection/DBManagerInterface";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Splitter, SplitterPanel } from "primereact/splitter";
import { Line } from "react-chartjs-2";
import { isCloud } from "../../App";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Tooltip,
  Legend,
  Title
);

const SPCDialog = ({
  visible,
  onHide,
  chartData = [],
  measuresMetadata = [],
  storeType,
}) => {
  const { t } = useTranslation();
  const [hasGraphType, setHasGraphType] = useState(false);
  const [limits, setLimits] = useState({});
  const [filteredMeasuresMetadata, setFilteredMeasuresMetadata] = useState([]);
  const [infoVisible, setInfoVisible] = useState(false);
  const [mychartData, setChartData] = useState(chartData);

  const measuresInfo = [
    { measure: "Measure", description: "The metric being analyzed" },
    { measure: "GraphType", description: "Type of graph used (if applicable)" },
    { measure: "N", description: "Sample size" },
    { measure: "Avg", description: "Average value" },
    { measure: "Median", description: "Middle value" },
    { measure: "StdDev", description: "Standard deviation" },
    { measure: "CV (%)", description: "Coefficient of Variation" },
    { measure: "Min", description: "Minimum value" },
    { measure: "Max", description: "Maximum value" },
    { measure: "Range", description: "Difference between max and min" },
    { measure: "LNL", description: "Lower natural limit" },
    { measure: "UNL", description: "Upper natural limit" },
    { measure: "USL", description: "Upper specification limit" },
    { measure: "Cp", description: "Process capability" },
    { measure: "Cpk", description: "Process capability index" },
  ];

  useEffect(() => {
    if (visible) {
      console.log(chartData);
      console.log(measuresMetadata);
      // first i have to sort chartData by TS ascending
      chartData.sort((a, b) => new Date(a.TS) - new Date(b.TS));


      setChartData(chartData);
      setHasGraphType(false);

      // Initialize limits from metadata (values saved in DB)
      const initialLimits = {};
      const filteredMeasures = measuresMetadata.filter(
        (m) =>
          m.Type?.toLowerCase() === "real" || m.Type?.toLowerCase() === "int"
      );

      setFilteredMeasuresMetadata(filteredMeasures);
      filteredMeasures.forEach((m) => {
        initialLimits[m.Name] = {
          USL: m.USL ?? null, // get USL from metadata
          LSL: m.LSL ?? null, // get LSL from metadata
        };
      });

      setLimits(initialLimits);
      setEditLimits(initialLimits); // also initialize edit state
    }
  }, [visible, measuresMetadata]);

  // Local state for editing in Settings tab
  const [editLimits, setEditLimits] = useState({ ...limits });

  const handleEditChange = (measureName, type, value) => {
    setEditLimits((prev) => ({
      ...prev,
      [measureName]: { ...prev[measureName], [type]: value },
    }));
  };

  const handleSave = () => {
    // Update all limits locally
    setLimits({ ...editLimits });

    const client = sessionStorage.getItem("chosenClient");
    const plant = sessionStorage.getItem("chosenPlant");

    // Filter measures by type (real or int)
    const filteredMeasures = measuresMetadata.filter(
      (m) => m.Type?.toLowerCase() === "real" || m.Type?.toLowerCase() === "int"
    );

    // Group measures by machine
    const machineGroups = {};
    filteredMeasures.forEach((m) => {
      const machine = m.Machine;
      if (!machineGroups[machine]) machineGroups[machine] = [];
      const limits = editLimits[m.Name];
      if (limits) {
        //if it is a curve the metadata also containst groupId and CurveOrderNumber
        if (m.CurveOrderNumber) {
          machineGroups[machine].push({
            MeasureName: m.Name,
            Type: m.Type,
            USL: limits.USL,
            LSL: limits.LSL,
            GroupId: m.GroupId,
            CurveOrderNumber: m.CurveOrderNumber,
          });
        } else {
          machineGroups[machine].push({
            MeasureName: m.Name,
            Type: m.Type,
            USL: limits.USL,
            LSL: limits.LSL,
          });
        }
        machineGroups[machine].push({
          MeasureName: m.Name,
          Type: m.Type,
          USL: limits.USL,
          LSL: limits.LSL,
        });
      }
    });

    // Send POST request for each machine
    Object.entries(machineGroups).forEach(([machine, metadata]) => {
      const payload = { Metadata: metadata };
      console.log("Updating limits with payload:", payload);
      const queryParams = `Client=${client}-${plant}&Machine=${machine}`;

      dbManagerInterface.SendPostRequest(
        `Automata/${encodeURIComponent(storeType)}/UpdateMetadata`,
        JSON.stringify(payload),
        (resp) => {
          if (resp?.success === false) {
            console.error(
              `Failed to update limits for machine ${machine}:`,
              resp
            );
          } else {
            console.log(
              `Limits updated successfully for machine ${machine}:`,
              resp
            );
          }
        },
        (error) => {
          console.error(`Error updating limits for machine ${machine}:`, error);
        },
        null,
        null,
        queryParams
      );
    });
  };

  // SPC Data
  const spcData = useMemo(() => {
    if (!visible || !mychartData?.length) return [];

    const results = [];



    filteredMeasuresMetadata.forEach((measure) => {
      const values = mychartData
        .map((d) => {
          var num;
          if (d[measure.Name]) {
            num = parseFloat(d[measure.Name]);
          } else if (d[measure.Machine + "-" + measure.Name]) {
            num = parseFloat(d[measure.Machine + "-" + measure.Name]);
          }
          return !isNaN(num) ? num : null;
        })
        .filter((v) => v !== null);
      if (values.length === 0) return;

      const n = values.length;
      const avg = (values.reduce((a, b) => a + b, 0) / n).toFixed(3);
      const variance = (
        values.reduce((a, b) => a + (b - avg) ** 2, 0) / n
      ).toFixed(3);
      const stdDev = Math.sqrt(variance).toFixed(3);
      const min = Math.min(...values).toFixed(3);
      const max = Math.max(...values).toFixed(3);
      const sorted = [...values].sort((a, b) => a - b);
      const median = (
        n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2
      ).toFixed(3);
      const mode = (() => {
        const freq = {};
        values.forEach((v) => (freq[v] = (freq[v] || 0) + 1));
        const maxCount = Math.max(...Object.values(freq));
        const modes = Object.keys(freq).filter((k) => freq[k] === maxCount);
        return modes.join(", ");
      })();
      const range = (max - min).toFixed(3);
      const cv = avg !== 0 ? ((stdDev / avg) * 100).toFixed(3) : 0;
      const avgNum = Number(avg);
      const stdDevNum = Number(stdDev);

      const unl = (avgNum + 3 * stdDevNum).toFixed(3);
      const lnl = (avgNum - 3 * stdDevNum).toFixed(3);

      if (measure.GraphType) setHasGraphType(true);

      // Cp/Cpk using current limits
      const USL = limits[measure.Name]?.USL;
      const LSL = limits[measure.Name]?.LSL;

      let Cp = "-";
      let Cpk = "-";

      if (USL !== null && LSL !== null && stdDevNum > 0) {
        const cpVal = (USL - LSL) / (6 * stdDevNum);
        const cpu = (USL - avgNum) / (3 * stdDevNum);
        const cpl = (avgNum - LSL) / (3 * stdDevNum);
        Cp = cpVal.toFixed(3);
        Cpk = Math.min(cpu, cpl).toFixed(3);
      }

      results.push({
        Measure: measure.Name,
        GraphType: measure.GraphType,
        Values: values,
        N: n,
        Avg: avg,
        Median: median,
        Mode: mode,
        StdDev: stdDev,
        CV: cv,
        Min: min,
        Max: max,
        Range: range,
        UNL: unl,
        LNL: lnl,
        USL: USL,
        LSL: LSL,
        Cp,
        Cpk,
      });
    });

    return results;
  }, [visible, mychartData, filteredMeasuresMetadata, limits]);

  // Settings Panel & Charts also use filteredMeasuresMetadata

  const renderDistributionChart = (measure) => {
    const { Values, Avg, StdDev, Measure } = measure;
    if (!Values || Values.length === 0) return null;
    if (Values.length < 2 || StdDev === 0) {
      return (
        <div className="text-center text-sm text-gray-500">
          {Measure}: {t("noVariation") || "No variation in data"}
        </div>
      );
    }

    const numBins = 10;
    const min = Math.min(...Values);
    const max = Math.max(...Values);
    if (min === max) {
      return (
        <div className="text-center text-sm text-gray-500">
          {Measure}: {t("noVariation") || "No variation in data"}
        </div>
      );
    }

    const binSize = (max - min) / numBins;
    const bins = new Array(numBins).fill(0);
    Values.forEach((v) => {
      const idx = Math.min(Math.floor((v - min) / binSize), numBins - 1);
      bins[idx]++;
    });
    const binCenters = bins.map((_, i) =>
      (min + i * binSize + binSize / 2).toFixed(2)
    );

    const gaussian = binCenters.map((x) => {
      const xVal = parseFloat(x);
      const exponent = -((xVal - Avg) ** 2) / (2 * StdDev ** 2);
      const gauss =
        (1 / (StdDev * Math.sqrt(2 * Math.PI))) * Math.exp(exponent);
      return gauss * Values.length * binSize * 1.2;
    });

    const data = {
      labels: binCenters,
      datasets: [
        {
          label: t("frequency") || "Frequency",
          data: bins,
          type: "bar",
          backgroundColor: "rgba(75,192,192,0.6)",
          borderColor: "rgba(75,192,192,1)",
          borderWidth: 1,
        },
        {
          label: "Gaussian Fit",
          data: gaussian,
          type: "line",
          borderColor: "rgba(255,99,132,1)",
          backgroundColor: "rgba(255,99,132,0.2)",
          borderWidth: 2,
          fill: false,
          tension: 0.2,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: "top" },
        title: {
          display: true,
          text: `${Measure} ${t("distribution") || "Distribution"}`,
        },
      },
      scales: {
        x: { title: { display: true, text: t("value") || "Value" } },
        y: { title: { display: true, text: t("count") || "Count" } },
      },
    };

    return (
      <div style={{ height: "240px" }}>
        <Bar data={data} options={options} />
      </div>
    );
  };

  const trendData = spcData.map((m, i) => {
    // Try to extract timestamps if available
    const labels = mychartData
      .map((d) => d.TS || d.Timestamp || d.DateTime || d.TimeStamp)
      .filter((ts) => ts !== undefined && ts !== null);

    // Fallback: if timestamps are missing, use entry numbers
    const effectiveLabels =
      labels.length === m.Values.length
        ? labels.map((ts) => new Date(ts).toLocaleString())
        : m.Values.map((_, idx) => idx + 1);

    const avgLine = new Array(m.Values.length).fill(parseFloat(m.Avg));
    const uslLine =
      m.USL !== null && m.USL !== undefined
        ? new Array(m.Values.length).fill(parseFloat(m.USL))
        : null;
    const lslLine =
      m.LSL !== null && m.LSL !== undefined
        ? new Array(m.Values.length).fill(parseFloat(m.LSL))
        : null;

    const data = {
      labels: effectiveLabels,
      datasets: [
        {
          label: `${m.Measure}`,
          data: m.Values,
          borderColor: "rgba(54,162,235,1)",
          backgroundColor: "rgba(54,162,235,0.2)",
          borderWidth: 2,
          tension: 0.25,
          pointRadius: 2,
          fill: false,
        },
        {
          label: "Average",
          data: avgLine,
          borderColor: "rgba(0,200,83,0.8)",
          borderDash: [6, 6],
          borderWidth: 1.5,
          pointRadius: 0,
          fill: false,
        },
        ...(uslLine
          ? [
              {
                label: "USL",
                data: uslLine,
                borderColor: "rgba(255,99,132,0.9)",
                borderDash: [4, 4],
                borderWidth: 1.5,
                pointRadius: 0,
                fill: false,
              },
            ]
          : []),
        ...(lslLine
          ? [
              {
                label: "LSL",
                data: lslLine,
                borderColor: "rgba(255,159,64,0.9)",
                borderDash: [4, 4],
                borderWidth: 1.5,
                pointRadius: 0,
                fill: false,
              },
            ]
          : []),
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" },
        title: {
          display: true,
          text: `${m.Measure} ${t("trend") || "Trend"}`,
        },
      },
      scales: {
        x: {
          title: { display: true, text: t("timestamp") || "Timestamp" },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 10,
          },
        },
        y: {
          title: { display: true, text: t("value") || "Value" },
        },
      },
    };

    return (
      <div
        key={i}
        className="p-3 bg-white rounded-lg shadow-sm border border-gray-100"
      >
        <div style={{ height: "240px" }}>
          <Line data={data} options={options} />
        </div>
      </div>
    );
  });

  return (
    <Dialog
      header={t("spcheader") || "SPC Analysis"}
      visible={visible}
      onHide={onHide}
      style={{ width: "90%" }}
    >
      <TabView>
        {/* Statistics Table */}
        <TabPanel header={t("statistics") || "Statistics"}>
          <Button
            icon="pi pi-info-circle"
            className="p-button-text p-button-sm"
            onClick={() => setInfoVisible(true)}
          />
          <Divider></Divider>
          <DataTable
            value={spcData}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            className="p-datatable-sm"
          >
            <Column field="Measure" header={t("measure")} frozen />
            {hasGraphType && (
              <Column field="GraphType" header={t("graphtype")} />
            )}
            <Column field="N" header="N" />
            <Column field="Avg" header="Avg" />
            <Column field="Median" header="Median" />
            <Column field="StdDev" header="StdDev" />
            <Column field="CV" header="CV (%)" />
            <Column field="Min" header="Min" />
            <Column field="Max" header="Max" />
            <Column field="Range" header="Range" />
            <Column field="LNL" header="LNL" />
            <Column field="UNL" header="UNL" />
            <Column field="USL" header="USL" />
            <Column field="Cp" header="Cp" />
            <Column field="Cpk" header="Cpk" />
          </DataTable>
        </TabPanel>
        {/* Distributions */}
        <TabPanel header={t("distributions") || "Distributions"}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-2">
            {spcData.map((m, i) => (
              <div
                key={i}
                className="p-2 bg-white rounded-lg shadow-sm border border-gray-100"
              >
                {renderDistributionChart(m)}
              </div>
            ))}
          </div>
        </TabPanel>
        {/* Trends Tab */}
        {/* Trends Tab */}
        <TabPanel header={t("trends") || "Trends"}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-2">
            {trendData}
          </div>
        </TabPanel>

        {isCloud && (
          <TabPanel header={t("settings") || "Settings"}>
            {/* Save Button */}
            <div className="flex justify-end mb-3">
              <Button
                label={t("save") || "Save"}
                icon="pi pi-save"
                onClick={() => handleSave()}
              />
            </div>

            <Divider className="my-3" />

            {/* Splitter with 4 columns */}
            <Splitter layout="orizontal">
              {/* Column 1 */}
              <SplitterPanel
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {visible &&
                  filteredMeasuresMetadata
                    .filter((_, i) => i % 4 === 0) // distribute measures to column 1
                    .map((m) => (
                      <Card
                        header={m.Name}
                        key={m.Name}
                        className="mb-4"
                        style={{
                          margin: "10px",
                          width: "100%",
                          fontWeight: "bold",
                          fontSize: "20px",
                          padding: "10px",
                        }}
                      >
                        <div className="mb-2">
                          <label className="block text-sm font-medium mb-1">
                            USL:
                          </label>
                          <InputNumber
                            value={editLimits[m.Name]?.USL}
                            onValueChange={(e) =>
                              handleEditChange(m.Name, "USL", e.value)
                            }
                            mode="decimal"
                            showButtons
                          />
                        </div>

                        <div className="mb-2">
                          <label className="block text-sm font-medium mb-1">
                            LSL:
                          </label>
                          <InputNumber
                            value={editLimits[m.Name]?.LSL}
                            onValueChange={(e) =>
                              handleEditChange(m.Name, "LSL", e.value)
                            }
                            mode="decimal"
                            showButtons
                            className="w-full"
                          />
                        </div>
                      </Card>
                    ))}
              </SplitterPanel>

              {/* Column 2 */}
              <SplitterPanel
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {visible &&
                  filteredMeasuresMetadata
                    .filter((_, i) => i % 4 === 1)
                    .map((m) => (
                      <Card
                        header={m.Name}
                        key={m.Name}
                        className="mb-4"
                        style={{
                          margin: "10px",
                          width: "100%",
                          fontWeight: "bold",
                          fontSize: "20px",
                          padding: "10px",
                        }}
                      >
                        <div className="mb-2">
                          <label className="block text-sm font-medium mb-1">
                            USL:
                          </label>
                          <InputNumber
                            value={editLimits[m.Name]?.USL}
                            onValueChange={(e) =>
                              handleEditChange(m.Name, "USL", e.value)
                            }
                            mode="decimal"
                            showButtons
                            className="w-full"
                          />
                        </div>

                        <div className="mb-2">
                          <label className="block text-sm font-medium mb-1">
                            LSL:
                          </label>
                          <InputNumber
                            value={editLimits[m.Name]?.LSL}
                            onValueChange={(e) =>
                              handleEditChange(m.Name, "LSL", e.value)
                            }
                            mode="decimal"
                            showButtons
                            className="w-full"
                          />
                        </div>
                      </Card>
                    ))}
              </SplitterPanel>

              {/* Column 3 */}
              <SplitterPanel
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {visible &&
                  filteredMeasuresMetadata
                    .filter((_, i) => i % 4 === 2)
                    .map((m) => (
                      <Card
                        header={m.Name}
                        key={m.Name}
                        className="mb-4"
                        style={{
                          margin: "10px",
                          width: "100%",
                          fontWeight: "bold",
                          fontSize: "20px",
                          padding: "10px",
                        }}
                      >
                        <div className="mb-2">
                          <label className="block text-sm font-medium mb-1">
                            USL:
                          </label>
                          <InputNumber
                            value={editLimits[m.Name]?.USL}
                            onValueChange={(e) =>
                              handleEditChange(m.Name, "USL", e.value)
                            }
                            mode="decimal"
                            showButtons
                            className="w-full"
                          />
                        </div>

                        <div className="mb-2">
                          <label className="block text-sm font-medium mb-1">
                            LSL:
                          </label>
                          <InputNumber
                            value={editLimits[m.Name]?.LSL}
                            onValueChange={(e) =>
                              handleEditChange(m.Name, "LSL", e.value)
                            }
                            mode="decimal"
                            showButtons
                            className="w-full"
                          />
                        </div>
                      </Card>
                    ))}
              </SplitterPanel>

              {/* Column 4 */}
              <SplitterPanel
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {visible &&
                  filteredMeasuresMetadata
                    .filter((_, i) => i % 4 === 3)
                    .map((m) => (
                      <Card
                        header={m.Name}
                        key={m.Name}
                        className="mb-4"
                        style={{
                          margin: "10px",
                          width: "100%",
                          fontWeight: "bold",
                          fontSize: "20px",
                          padding: "10px",
                        }}
                      >
                        <div className="mb-2">
                          <label className="block text-sm font-medium mb-1">
                            USL:
                          </label>
                          <InputNumber
                            value={editLimits[m.Name]?.USL}
                            onValueChange={(e) =>
                              handleEditChange(m.Name, "USL", e.value)
                            }
                            mode="decimal"
                            showButtons
                            className="w-full"
                          />
                        </div>

                        <div className="mb-2">
                          <label className="block text-sm font-medium mb-1">
                            LSL:
                          </label>
                          <InputNumber
                            value={editLimits[m.Name]?.LSL}
                            onValueChange={(e) =>
                              handleEditChange(m.Name, "LSL", e.value)
                            }
                            mode="decimal"
                            showButtons
                            className="w-full"
                          />
                        </div>
                      </Card>
                    ))}
              </SplitterPanel>
            </Splitter>
          </TabPanel>
        )}
      </TabView>
      <Dialog
        header="Measure Definitions"
        visible={infoVisible}
        style={{ width: "50%" }}
        onHide={() => setInfoVisible(false)}
      >
        <DataTable value={measuresInfo} responsiveLayout="scroll">
          <Column field="measure" header="Measure" />
          <Column field="description" header="Description" />
        </DataTable>
      </Dialog>
    </Dialog>
  );
};

export default SPCDialog;
